import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detectar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Detectar iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listener para evento de instalação
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Mostrar banner após 5 segundos
      setTimeout(() => {
        setShowBanner(true);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Verificar se foi instalado
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setShowBanner(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowInstallPrompt(true);
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setShowBanner(false);
    }
    
    setDeferredPrompt(null);
  };

  const closeBanner = () => {
    setShowBanner(false);
    // Não mostrar novamente nesta sessão
    sessionStorage.setItem('installBannerDismissed', 'true');
  };

  // Não mostrar se já está instalado ou se foi dispensado
  if (isInstalled || sessionStorage.getItem('installBannerDismissed')) {
    return null;
  }

  return (
    <>
      {/* Botão fixo de instalação */}
      {!isInstalled && (deferredPrompt || isIOS) && !showBanner && (
        <button
          onClick={handleInstallClick}
          className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center space-x-2 animate-bounce"
          data-testid="install-pwa-button"
        >
          <Download className="w-5 h-5" />
          <span className="font-medium">Instalar App</span>
        </button>
      )}

      {/* Banner de instalação */}
      {showBanner && !isInstalled && (
        <div className="fixed top-4 left-4 right-4 z-50 animate-slide-down">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-md mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">
                    Instale o Needi
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Tenha acesso rápido e trabalhe offline instalando nosso app!
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={handleInstallClick}
                      size="sm"
                      className="bg-gradient-to-r from-indigo-600 to-purple-600"
                      data-testid="install-banner-button"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Instalar
                    </Button>
                    <Button
                      onClick={closeBanner}
                      size="sm"
                      variant="outline"
                      data-testid="dismiss-banner-button"
                    >
                      Agora não
                    </Button>
                  </div>
                </div>
              </div>
              <button
                onClick={closeBanner}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de instruções para iOS */}
      {showInstallPrompt && isIOS && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Instalar Needi no iOS
              </h3>
              <button
                onClick={() => setShowInstallPrompt(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4 text-sm text-gray-700">
              <p className="font-medium">Para instalar o app no seu iPhone/iPad:</p>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 text-indigo-600 font-bold text-xs">
                  1
                </div>
                <p>Toque no botão <strong>Compartilhar</strong> (ícone de quadrado com seta) na barra inferior do Safari</p>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 text-indigo-600 font-bold text-xs">
                  2
                </div>
                <p>Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong></p>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 text-indigo-600 font-bold text-xs">
                  3
                </div>
                <p>Toque em <strong>"Adicionar"</strong> no canto superior direito</p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800">
                💡 Após instalar, você pode abrir o Needi direto da tela inicial como um app nativo!
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
